<template>
  <b-modal
    v-model="show"
    title="Nueva escuela"
    size="xl"
    @hidden="onClose"
    @ok="onSubmit"
    :ok-disabled="guardando"
    ok-title="Guardar"
    cancel-title="Cancelar"
  >
    <b-form @submit.prevent="onSubmit">
      <b-row>
        <b-col md="12">
          <b-form-group label="Nombre de la escuela *">
            <b-form-input
              v-model="form.nombre"
              placeholder="Ingrese el nombre"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Dominio *">
            <b-form-input
              v-model="form.domain"
              placeholder="ejemplo.com"
              required
              @blur="cargarUsuariosPorDominio"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Dirección">
            <b-form-input
              v-model="form.direccion"
              placeholder="Dirección completa"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Ciudad *">
            <b-form-input
              v-model="form.ciudad"
              placeholder="Ciudad"
              required
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Teléfono">
            <b-form-input
              v-model="form.telefono"
              placeholder="Teléfono"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Total de Licencias">
            <b-form-input
              v-model.number="form.cantidad"
              type="number"
              placeholder="0"
              min="0"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Estatus *">
            <b-form-select
              v-model="form.estatus"
              :options="estatusOptions"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Administrador de la escuela">
            <b-form-select
              v-model="form.admin_id"
              :options="usuariosOptions"
              :disabled="cargandoUsuarios || !form.domain"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ cargandoUsuarios ? 'Cargando usuarios...' : '-- Seleccione un usuario --' }}
                </b-form-select-option>
              </template>
            </b-form-select>
            <small class="text-muted" v-if="!form.domain">
              Primero ingrese un dominio para cargar los usuarios disponibles
            </small>
            <small class="text-muted" v-else-if="usuariosOptions.length === 0 && !cargandoUsuarios">
              No hay usuarios disponibles para el dominio {{ form.domain }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Correo Coach">
            <b-form-input
              v-model="form.correoCoach"
              type="email"
              placeholder="coach@escuela.com"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Correo IP Manager">
            <b-form-input
              v-model="form.correoIpManager"
              type="email"
              placeholder="ipmanager@escuela.com"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancelar
      </b-button>
      <b-button 
        variant="primary" 
        @click="ok()"
        :disabled="guardando"
      >
        <b-spinner small v-if="guardando" class="mr-1"></b-spinner>
        {{ guardando ? 'Guardando...' : 'Guardar Escuela' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import EscuelaService from '@/services/EscuelaService';
import { validateDomain, validateEmail, validateRequired } from '@/utils/validators';

export default {
  name: 'CreateEscuelaModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      guardando: false,
      cargandoUsuarios: false,
      usuariosOptions: [],
      form: {
        nombre: '',
        domain: '',
        direccion: '',
        ciudad: '',
        telefono: '',
        cantidad: 0,
        correoCoach: '',
        correoIpManager: '',
        estatus: 1,
        admin_id: null
      },
      estatusOptions: [
        { value: 1, text: 'Activa' },
        { value: 0, text: 'Inactiva' }
      ]
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  methods: {
    async cargarUsuariosPorDominio() {
      if (!this.form.domain || this.form.domain.trim() === '') {
        this.usuariosOptions = [];
        return;
      }

      // Verificar si el dominio ya existe
      this.cargandoUsuarios = true;
      try {
        const verificacion = await EscuelaService.verificarDominio(this.form.domain);
        const { disponible } = verificacion.data;

        if (!disponible) {
          this.$bvToast.toast('Este dominio ya está registrado. Por favor, use un dominio diferente.', {
            title: 'Dominio no disponible',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-center'
          });
          this.usuariosOptions = [];
          this.form.domain = '';
          return;
        }

        // Si está disponible, cargar usuarios
        const response = await EscuelaService.getUsuariosPorDominio(this.form.domain);
        const usuarios = response.data.data || response.data.resultSet || [];
        
        this.usuariosOptions = usuarios.map(user => ({
          value: user.id,
          text: `${user.name} (${user.email})`
        }));

        // Si la respuesta incluye información de la escuela con admin, establecerlo
        if (response.data.escuela && response.data.escuela.admin) {
          const adminId = parseInt(response.data.escuela.admin);
          // Verificar que el admin esté en la lista de usuarios
          if (this.usuariosOptions.some(u => u.value === adminId)) {
            this.form.admin_id = adminId;
          }
        }
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        this.$bvToast.toast('Error al verificar el dominio o cargar usuarios', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-center'
        });
        this.usuariosOptions = [];
      } finally {
        this.cargandoUsuarios = false;
      }
    },
    async onSubmit(evt) {
      if (evt) evt.preventDefault();
      
      this.guardando = true;
      
      // Validar campos obligatorios
      const nombreValidation = validateRequired(this.form.nombre, 'El nombre de la escuela');
      if (!nombreValidation.valid) {
        this.$bvToast.toast(nombreValidation.error, {
          title: 'Error de validación',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-center'
        });
        this.guardando = false;
        return;
      }

      // Validar dominio
      const domainValidation = validateDomain(this.form.domain, true);
      if (!domainValidation.valid) {
        this.$bvToast.toast(domainValidation.error, {
          title: 'Error de validación',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-center'
        });
        this.guardando = false;
        return;
      }

      const ciudadValidation = validateRequired(this.form.ciudad, 'La ciudad');
      if (!ciudadValidation.valid) {
        this.$bvToast.toast(ciudadValidation.error, {
          title: 'Error de validación',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-center'
        });
        this.guardando = false;
        return;
      }

      // Validar correoCoach si está presente
      if (this.form.correoCoach) {
        const correoCoachValidation = validateEmail(this.form.correoCoach, false);
        if (!correoCoachValidation.valid) {
          this.$bvToast.toast('El correo del Coach debe ser un email válido', {
            title: 'Error de validación',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-center'
          });
          this.guardando = false;
          return;
        }
      }

      // Validar correoIpManager si está presente
      if (this.form.correoIpManager) {
        const correoIpManagerValidation = validateEmail(this.form.correoIpManager, false);
        if (!correoIpManagerValidation.valid) {
          this.$bvToast.toast('El correo del IP Manager debe ser un email válido', {
            title: 'Error de validación',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-center'
          });
          this.guardando = false;
          return;
        }
      }
      // Validar email si está presente
      if (this.form.email) {
        const emailValidation = validateEmail(this.form.email, false);
        if (!emailValidation.valid) {
          this.$bvToast.toast(emailValidation.error, {
            title: 'Error de validación',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-center'
          });
          this.guardando = false;
          return;
        }
      }

      try {
        await EscuelaService.createEscuela(this.form);
        
        this.$bvToast.toast('Escuela creada exitosamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-center'
        });

        this.resetForm();
        this.$emit('created');
        this.show = false;
      } catch (error) {
        console.error('Error al crear escuela:', error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          // Extraer el primer error de cada campo
          const errors = error.response.data.errors;
          const errorMessages = Object.values(errors).flat();
          
          errorMessages.forEach(message => {
            this.$bvToast.toast(message, {
              title: 'Error de validación',
              variant: 'warning',
              solid: true,
              toaster: 'b-toaster-bottom-center'
            });
          });
        } else {
          const errorMessage = error.response?.data?.message || 'Error al crear la escuela';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-center'
          });
        }
      } finally {
        this.guardando = false;
      }
    },
    onClose() {
      this.resetForm();
      this.$emit('input', false);
    },
    resetForm() {
      this.form = {
        nombre: '',
        domain: '',
        direccion: '',
        ciudad: '',
        telefono: '',
        cantidad: 0,
        correoCoach: '',
        correoIpManager: '',
        estatus: 1,
        admin_id: null
      };
      this.usuariosOptions = [];
    }
  }
};
</script>
